<template>
  <div class="downloads-container">
    <div class="downloads-header">
      <h1>资料下载</h1>
      <p>获取最新的产品资料、技术文档和解决方案</p>
    </div>

    <div class="downloads-content">
      <!-- 资料分类组件 -->
      <div class="downloads-categories">
        <div
          :class="['category-item', { active: activeCategoryId === 'all' }]"
          @click="switchCategory({ id: 'all', name: '全部' })"
        >
          全部
        </div>
        <div
          v-for="category in categories"
          :key="category.id"
          :class="['category-item', { active: activeCategoryId === category.id }]"
          @click="switchCategory(category)"
        >
          {{ category.name }}
        </div>
      </div>

      <!-- 资料列表组件 -->
      <div class="downloads-list">
        <div v-for="item in downloads" :key="item.id" class="download-item" @click="goToDetail(item)">
          <div class="download-image" v-if="item.headImgUrl">
            <img :src="item.headImgUrl" :alt="item.title" referrerpolicy="no-referrer">
          </div>
          <div class="download-info">
            <div class="download-type">{{ item.catName || currentCategoryName }}</div>
            <h3 class="download-title">{{ item.title }}</h3>
            <p class="download-date">{{ formatDate(item) }}</p>
            <p class="download-desc">{{ item.secTitle || getPlainText(item.content) }}</p>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="downloads.length === 0" class="empty-state">
        <p>暂无相关资料</p>
      </div>
    </div>
  </div>
</template>

<script>
import { articleApi, articleCatApi } from '../api'

const PARENT_CAT_ID = 18 // 资料下载父分类ID

export default {
  name: 'Downloads',
  data() {
    return {
      downloads: [],
      categories: [],
      activeCategoryId: 'all',
      loading: false
    }
  },
  computed: {
    currentCategoryName() {
      if (this.activeCategoryId === 'all') return '全部'
      const current = this.categories.find(item => item.id === this.activeCategoryId)
      return current ? current.name : ''
    }
  },
  mounted() {
    this.fetchCategories()
  },
  methods: {
    async fetchCategories() {
      try {
        const response = await articleCatApi.dataByPid(PARENT_CAT_ID)
        const categoryList = this.parseCategoryList(response)
        this.categories = categoryList
        await this.fetchDownloadsByCatId(PARENT_CAT_ID)
      } catch (error) {
        console.error('获取资料分类失败:', error)
      }
    },
    async switchCategory(category) {
      if (!category || category.id === this.activeCategoryId) {
        return
      }
      this.activeCategoryId = category.id
      const catId = category.id === 'all' ? PARENT_CAT_ID : category.id
      await this.fetchDownloadsByCatId(catId)
    },
    async fetchDownloadsByCatId(catId) {
      if (!catId) {
        this.downloads = []
        return
      }
      this.loading = true
      try {
        const response = await articleApi.data({ catId })
        this.downloads = this.parseDownList(response)
      } catch (error) {
        console.error('获取资料列表失败:', error)
        this.downloads = []
      } finally {
        this.loading = false
      }
    },
    parseCategoryList(response) {
      if (response && (response.code === 0 || response.code === 200) && Array.isArray(response.data)) {
        return response.data
      }
      if (Array.isArray(response)) {
        return response
      }
      return []
    },
    parseDownList(response) {
      if (response && (response.code === 0 || response.code === 200) && Array.isArray(response.data)) {
        return response.data
      }
      if (Array.isArray(response)) {
        return response
      }
      return []
    },
    getPlainText(html) {
      if (!html) return ''
      return html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/\s+/g, ' ')
        .trim()
    },
    formatDate(item) {
      const rawDate = item.titleDate || item.crTime || item.upTime
      if (!rawDate) return ''
      const date = new Date(rawDate)
      if (Number.isNaN(date.getTime())) {
        return String(rawDate).slice(0, 10)
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    goToDetail(item) {
      if (!item || !item.id) return
      window.dispatchEvent(new CustomEvent('switchPage', {
        detail: { page: 'article', articleId: item.id }
      }))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.downloads-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.downloads-header {
  text-align: center;
  margin-bottom: 40px;
}

.downloads-header h1 {
  font-size: 36px;
  color: #333;
  margin-bottom: 10px;
}

.downloads-header p {
  font-size: 16px;
  color: #666;
}

.downloads-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.downloads-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 15px;
}

.category-item {
  padding: 8px 20px;
  background-color: #f5f5f5;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.category-item:hover {
  background-color: #e0e0e0;
}

.category-item.active {
  background-color: #1890ff;
  color: white;
}

.downloads-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.download-item {
  display: flex;
  gap: 20px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.download-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.download-image {
  flex: 0 0 200px;
  height: 140px;
  overflow: hidden;
  border-radius: 4px;
}

.download-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.download-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.download-type {
  display: inline-block;
  padding: 2px 10px;
  background-color: #e6f7ff;
  color: #1890ff;
  font-size: 12px;
  border-radius: 10px;
  align-self: flex-start;
}

.download-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.download-date {
  font-size: 14px;
  color: #999;
  margin: 0;
}

.download-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #eee;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .download-item {
    flex-direction: column;
  }

  .download-image {
    flex: none;
    width: 100%;
    height: 200px;
  }

  .downloads-categories {
    justify-content: center;
  }

  .downloads-header h1 {
    font-size: 28px;
  }
}

@media (max-width: 480px) {
  .downloads-container {
    padding: 20px 15px;
  }

  .downloads-header h1 {
    font-size: 24px;
  }

  .download-image {
    height: 150px;
  }

  .category-item {
    padding: 6px 16px;
    font-size: 12px;
  }
}
</style>